import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Clock,
  Globe,
  Server,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { checkUptime, type UptimeResult } from "@/lib/uptime.functions";

export const Route = createFileRoute("/uptime")({
  head: () => ({
    meta: [
      { title: "Uptime monitoring — Elvion Group Nederland" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Live uptime monitoring voor elvion-group.nl na de nameserver-wijziging.",
      },
    ],
  }),
  component: UptimePage,
});

function formatDuration(ms: number | null) {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function StatusBadge({ status }: { status: UptimeResult["endpoints"][number]["status"] }) {
  if (status === "up") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
        <CheckCircle2 className="h-4 w-4" />
        Online
      </span>
    );
  }
  if (status === "down") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700">
        <XCircle className="h-4 w-4" />
        Offline
      </span>
    );
  }
  if (status === "timeout") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
        <Clock className="h-4 w-4" />
        Timeout
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
      <AlertTriangle className="h-4 w-4" />
      Fout
    </span>
  );
}

function EndpointCard({ endpoint }: { endpoint: UptimeResult["endpoints"][number] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
            <h2 className="truncate text-base font-semibold text-foreground">{endpoint.label}</h2>
          </div>
          <a
            href={endpoint.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 block truncate text-sm text-primary hover:underline"
          >
            {endpoint.url}
          </a>
        </div>
        <StatusBadge status={endpoint.status} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm">
        <div>
          <div className="text-muted-foreground">HTTP-status</div>
          <div className="font-mono font-medium text-foreground">
            {endpoint.httpStatus ?? "—"}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Responstijd</div>
          <div className="font-mono font-medium text-foreground">
            {formatDuration(endpoint.durationMs)}
          </div>
        </div>
      </div>

      {endpoint.redirectLocation && (
        <div className="mt-3 text-sm text-amber-700">
          Doorverwezen naar: {" "}
          <a
            href={endpoint.redirectLocation}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {endpoint.redirectLocation}
          </a>
        </div>
      )}

      {endpoint.error && (
        <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {endpoint.error}
        </div>
      )}
    </div>
  );
}

function DnsCard({ dns }: { dns: UptimeResult["dns"] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Nameservers (DNS)</h2>
        </div>
        {dns.matchesExpected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            Cloudflare
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            Nog niet over
          </span>
        )}
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <div className="text-sm text-muted-foreground">Huidige nameservers voor elvion-group.nl</div>
        {dns.nameservers.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {dns.nameservers.map((ns) => (
              <li
                key={ns}
                className={`font-mono text-sm ${
                  EXPECTED_NS.has(ns) ? "text-emerald-700" : "text-foreground"
                }`}
              >
                {ns}
                {EXPECTED_NS.has(ns) && (
                  <CheckCircle2 className="ml-2 inline h-3.5 w-3.5 text-emerald-600" />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-2 text-sm text-slate-700">Geen nameservers gevonden.</div>
        )}

        <div className="mt-4 text-xs text-muted-foreground">
          Verwacht: lou.ns.cloudflare.com & suzanne.ns.cloudflare.com
        </div>

        {dns.error && (
          <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">{dns.error}</div>
        )}
      </div>
    </div>
  );
}

const EXPECTED_NS = new Set(["lou.ns.cloudflare.com", "suzanne.ns.cloudflare.com"]);

function UptimePage() {
  const fetchUptime = useServerFn(checkUptime);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const query = useQuery<UptimeResult>({
    queryKey: ["uptime-check"],
    queryFn: () => fetchUptime({ data: undefined }),
    refetchInterval: autoRefresh ? 30_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const data = query.data;
  const isLoading = query.isFetching && !query.isRefetching;

  useEffect(() => {
    document.title = "Uptime monitoring — Elvion Group Nederland";
  }, []);

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar home
            </Link>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Uptime monitoring
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Controleer of elvion-group.nl live is na de nameserver-wijziging.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              Auto-refresh (30s)
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              {query.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Verversen
            </Button>
          </div>
        </div>

        {data && (
          <div className="mb-4 text-sm text-muted-foreground">
            Laatste controle: {" "}
            <span className="font-mono font-medium text-foreground">
              {formatTime(data.checkedAt)}
            </span>
          </div>
        )}

        {query.isError && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Er ging iets mis bij het ophalen van de status: {(query.error as Error).message}
          </div>
        )}

        {isLoading && !data && (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.endpoints.map((endpoint) => (
                <EndpointCard key={endpoint.id} endpoint={endpoint} />
              ))}
            </div>

            <DnsCard dns={data.dns} />

            <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Tip:</strong> laat dit tabblad open staan
                tijdens de nameserver-wijziging. Zodra de nameservers op Cloudflare staan én
                elvion-group.nl online is, is de migratie voltooid. Maximaal 24 uur kan het
                duren voordat DNS wereldwijd is bijgewerkt.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
